import React, { useCallback, useEffect, useState } from 'react';
import { FiAlertTriangle, FiCoffee, FiLogIn, FiLogOut, FiPlay } from 'react-icons/fi';
import type { HREmployee, HRShift } from './hrData';
import { usDate } from './licenseUtils';
import {
  clockIn, clockOut, endBreak, getTimesheet, localDateKey, startBreak, weekStartKey, type MealBreakAnswer,
} from '../../services/hrService';
import '../../styles/TimeClock.css';

/**
 * Clock in / breaks / clock out for the signed-in person — in the HR Center's
 * Clock tab and on My Day. Loads this week's shifts itself, so the hours and
 * overtime warning are always for the current week whatever week the
 * timesheet beside it is showing.
 *
 * Everyone is treated as hourly (non-exempt) for the overtime warning: the API
 * has no exempt flag yet. [NEEDS VERIFICATION — backend]
 */

interface Props {
  me: HREmployee;
  /** Hides today's punch list (for tight spots). */
  compact?: boolean;
  /** Bump to reload, e.g. after an admin corrects a shift. */
  refreshKey?: number;
  /** Called after a clock action succeeds, so the caller can reload too. */
  onChanged?: () => void;
}

const OVERTIME_MIN = 40 * 60;
const NEAR_OVERTIME_MIN = 36 * 60;
/** Clocking out after this many worked minutes asks about the meal break. */
const MEAL_QUESTION_AFTER_MIN = 5 * 60;

export const timeLabel = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
export const isoTime = (iso: string | null) => (iso ? timeLabel(new Date(iso)) : '—');
export const hoursLabel = (minutes: number) => `${Math.floor(minutes / 60)}h ${String(Math.round(minutes % 60)).padStart(2, '0')}m`;

/** Worked minutes — counted live (minus breaks) while a shift is still open. */
export const workedMinutes = (s: HRShift, now: Date) => {
  if (s.workedMinutes !== null) return s.workedMinutes;
  const openBreak = s.breakStartedAt ? (now.getTime() - new Date(s.breakStartedAt).getTime()) / 60000 : 0;
  return Math.max(0, (now.getTime() - new Date(s.clockInAt).getTime()) / 60000 - s.breakMinutes - openBreak);
};

const TimeClockCard: React.FC<Props> = ({ me, compact, refreshKey = 0, onChanged }) => {
  const [shifts, setShifts] = useState<HRShift[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());
  const [asking, setAsking] = useState(false);
  const [mealNote, setMealNote] = useState('');

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    try {
      setShifts(await getTimesheet(weekStartKey()));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your shifts.');
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      setNow(new Date());
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const today = localDateKey(now);
  const weekStart = weekStartKey(now);
  const mine = (shifts ?? []).filter(s => s.employeeId === me.id);
  // Open shifts come back for any week, so "clocked in" never depends on the week.
  const openShift = mine.find(s => !s.clockOutAt);
  const status: 'in' | 'break' | 'out' = !openShift ? 'out' : openShift.breakStartedAt ? 'break' : 'in';
  const staleShift = openShift && openShift.workDate !== today;
  // Today's latest shift (the open one, or the last one finished today) for the punch list.
  const todayShift = openShift ?? [...mine].filter(s => s.workDate === today).sort((a, b) => b.clockInAt.localeCompare(a.clockInAt))[0];
  const worked = mine.filter(s => s.workDate === today || s === openShift).reduce((n, s) => n + workedMinutes(s, now), 0);
  const onBreakFor = openShift?.breakStartedAt ? (now.getTime() - new Date(openShift.breakStartedAt).getTime()) / 60000 : 0;
  const week = mine.filter(s => s.workDate >= weekStart).reduce((n, s) => n + workedMinutes(s, now), 0);
  const overtime = week >= OVERTIME_MIN;
  const nearOvertime = !overtime && week >= NEAR_OVERTIME_MIN;
  const disabled = busy || !shifts;

  const requestClockOut = () => {
    if (openShift && workedMinutes(openShift, now) >= MEAL_QUESTION_AFTER_MIN) setAsking(true);
    else run(() => clockOut());
  };
  const answer = (taken: boolean) => {
    const meal: MealBreakAnswer = { mealBreakTaken: taken };
    if (!taken && mealNote.trim()) meal.mealBreakNote = mealNote.trim();
    setAsking(false);
    setMealNote('');
    run(() => clockOut(meal));
  };

  const statusLabel = status === 'in' ? 'Working' : status === 'break' ? 'On break' : 'Clocked out';

  return (
    <div className={`tclock glass-frost tclock--${status}${compact ? ' tclock--compact' : ''}`}>
      <span className="tclock__glow" aria-hidden="true" />
      <div className="tclock__top">
        <span className={`tclock__pill tclock__pill--${status}`}>
          <span className="tclock__pulse" aria-hidden="true" />{statusLabel}
        </span>
        <span className="tclock__now">{timeLabel(now)}</span>
      </div>

      {error && (
        <div className="hr__alert" role="alert">
          <span>{error}</span>
          <button type="button" className="hr__alert-action" onClick={() => (shifts ? setError(null) : load())}>
            {shifts ? 'Dismiss' : 'Retry'}
          </button>
        </div>
      )}

      <div className="tclock__main">
        <div className="tclock__big" aria-live="polite">
          {hoursLabel(worked)}
          <span className="tclock__big-label">{!shifts ? 'Loading…' : status === 'out' && !todayShift ? 'Not clocked in today' : 'worked today'}</span>
        </div>
        {status === 'break' && <p className="tclock__sub">On break for {Math.round(onBreakFor)} min</p>}
        {openShift && status !== 'break' && <p className="tclock__sub">Since {isoTime(openShift.clockInAt)}</p>}
        {staleShift && (
          <p className="tclock__warn"><FiAlertTriangle size={12} aria-hidden="true" />You didn&apos;t clock out on {usDate(openShift!.workDate)}. Clock out now and ask your admin to fix the time.</p>
        )}
      </div>

      {asking ? (
        <div className="tclock__ask" role="group" aria-label="Meal break check">
          <p className="tclock__ask-q">Did you get your full, uninterrupted meal break today?</p>
          <input
            className="tclock__ask-note"
            value={mealNote}
            onChange={e => setMealNote(e.target.value)}
            placeholder="If not, what happened? (optional)"
            aria-label="Why you missed your meal break"
          />
          <div className="tclock__actions">
            <button type="button" className="tclock__btn" disabled={busy} onClick={() => answer(false)}>No</button>
            <button type="button" className="tclock__btn tclock__btn--primary" disabled={busy} onClick={() => answer(true)}>Yes, clock out</button>
          </div>
        </div>
      ) : (
        <div className="tclock__actions">
          {status === 'out' && (
            <button type="button" className="tclock__btn tclock__btn--primary tclock__btn--wide" disabled={disabled} onClick={() => run(() => clockIn())}>
              <FiLogIn size={15} aria-hidden="true" />Clock in
            </button>
          )}
          {status === 'in' && (
            <>
              <button type="button" className="tclock__btn" disabled={disabled} onClick={() => run(startBreak)}><FiCoffee size={14} aria-hidden="true" />Start break</button>
              <button type="button" className="tclock__btn tclock__btn--danger" disabled={disabled} onClick={requestClockOut}><FiLogOut size={14} aria-hidden="true" />Clock out</button>
            </>
          )}
          {status === 'break' && (
            <>
              <button type="button" className="tclock__btn tclock__btn--primary" disabled={disabled} onClick={() => run(endBreak)}><FiPlay size={14} aria-hidden="true" />End break</button>
              <button type="button" className="tclock__btn tclock__btn--danger" disabled={disabled} onClick={requestClockOut}><FiLogOut size={14} aria-hidden="true" />Clock out</button>
            </>
          )}
        </div>
      )}

      <div className="tclock__foot">
        <div className="tclock__week">
          <span>This week</span>
          <strong>{hoursLabel(week)}</strong>
          <span className="tclock__week-track" aria-hidden="true">
            <span className={`tclock__week-fill${overtime ? ' tclock__week-fill--over' : nearOvertime ? ' tclock__week-fill--near' : ''}`} style={{ width: `${Math.min(100, (week / OVERTIME_MIN) * 100)}%` }} />
          </span>
        </div>
        {overtime && <p className="tclock__warn"><FiAlertTriangle size={12} aria-hidden="true" />You&apos;re in overtime this week ({hoursLabel(week - OVERTIME_MIN)} over 40h).</p>}
        {nearOvertime && <p className="tclock__note">{hoursLabel(OVERTIME_MIN - week)} until overtime this week.</p>}
        {!compact && todayShift && (
          // The API keeps total break minutes, not each break, so breaks show as one line.
          <ul className="tclock__log" aria-label="Today's punches">
            <li><span>Clocked in</span><span>{isoTime(todayShift.clockInAt)}</span></li>
            {todayShift.breakMinutes > 0 && <li><span>Breaks</span><span>{Math.round(todayShift.breakMinutes)} min</span></li>}
            {todayShift.breakStartedAt && <li><span>Break</span><span>{isoTime(todayShift.breakStartedAt)} – now</span></li>}
            {todayShift.clockOutAt && <li><span>Clocked out</span><span>{isoTime(todayShift.clockOutAt)}</span></li>}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TimeClockCard;
